export type BilagApprovalStatus = 'pending' | 'submitted' | 'approved' | 'rejected' | 'revised';
export type BilagSyncStatus = 'not_synced' | 'syncing' | 'synced' | 'sync_failed';
export type BilagSource = 'manual' | 'efaktura';
export type BilagCategory = 'materials' | 'tools' | 'transport' | 'subcontractor' | 'office' | 'other';

export const BILAG_APPROVAL_STATUS_LABELS: Record<BilagApprovalStatus, string> = {
    pending: 'Afventer',
    submitted: 'Indsendt',
    approved: 'Godkendt',
    rejected: 'Afvist',
    revised: 'Revideret',
};

export const BILAG_APPROVAL_STATUS_COLORS: Record<BilagApprovalStatus, string> = {
    pending: 'bg-amber-100 text-amber-700',
    submitted: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    revised: 'bg-purple-100 text-purple-700',
};

export const BILAG_SYNC_STATUS_LABELS: Record<BilagSyncStatus, string> = {
    not_synced: 'Ikke synkroniseret',
    syncing: 'Synkroniserer...',
    synced: 'Synkroniseret',
    sync_failed: 'Synkronisering fejlet',
};

export const BILAG_SYNC_STATUS_COLORS: Record<BilagSyncStatus, string> = {
    not_synced: 'bg-gray-100 text-gray-700',
    syncing: 'bg-blue-100 text-blue-700',
    synced: 'bg-green-100 text-green-700',
    sync_failed: 'bg-red-100 text-red-700',
};

export const BILAG_CATEGORY_LABELS: Record<BilagCategory, string> = {
    materials: 'Materialer',
    tools: 'Værktøj',
    transport: 'Transport',
    subcontractor: 'Underentreprenør',
    office: 'Kontor',
    other: 'Andet',
};

export interface BilagModel extends CreateBilagModel {
    id: string;
    createdAt: string;
    updatedAt: string;

    // File info
    fileUrl: string;
    fileSize: number;
    mimeType: string;
    storagePath: string;

    // Approval workflow
    approvalStatus: BilagApprovalStatus;
    submittedAt?: string;
    submittedBy?: string;
    approvedAt?: string;
    approvedBy?: string;
    approvedByName?: string;
    rejectedAt?: string;
    rejectedBy?: string;
    rejectedByName?: string;
    rejectionReason?: string;

    // ERP sync
    syncStatus: BilagSyncStatus;
    syncedAt?: string;
    syncError?: string;
    dineroVoucherId?: string;
    economicVoucherNumber?: number;

    // E-faktura origin (absent on older APIs = manual bilag)
    source?: BilagSource;
    creditorInvoiceId?: string;
    /** False for an e-faktura bilag without a PDF. Absent = has a file. */
    hasFile?: boolean;
    sourceInvoiceTotalInclVat?: number;
    sourceInvoiceProjectCount?: number;
}

export interface CreateBilagModel {
    userId: string;
    userName: string;
    projectId: string;
    projectTitle: string;
    companyId: string;

    description: string;
    supplier: string;
    invoiceNumber?: string;
    receiptDate: string;

    amountExclVat: number;
    vatAmount: number;
    totalAmount: number;
    currency: string; // 'DKK'

    category: BilagCategory;
    accountNumber?: number;
    linkedMaterialIds?: string[];
    fileName: string;
}

// Helper to format bilag price
export const formatBilagPrice = (price: number): string => {
    return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(price);
};

// Helper to check if a bilag can be edited
export const canEditBilag = (
    bilag: BilagModel,
    userId: string,
    isProjectLeader: boolean
): boolean => {
    // Project leaders can always edit
    if (isProjectLeader) return true;

    // Creator can edit if pending or rejected
    if (bilag.userId === userId) {
        return bilag.approvalStatus === 'pending' ||
            bilag.approvalStatus === 'rejected';
    }

    return false;
};

// Helper to check if a user can approve/reject a bilag
export const canApproveBilag = (
    bilag: BilagModel,
    isProjectLeader: boolean
): boolean => {
    if (!isProjectLeader) return false;
    // Can approve bilag that are pending or submitted
    return bilag.approvalStatus === 'pending' ||
        bilag.approvalStatus === 'submitted' ||
        bilag.approvalStatus === 'revised';
};

// True for a bilag created from an approved Sproom e-invoice
export const isEfakturaBilag = (bilag: Pick<BilagModel, 'source'>): boolean =>
    bilag.source === 'efaktura';

// E-faktura bilag cannot be deleted directly; they belong to the e-invoice
export const canDeleteBilag = (bilag: Pick<BilagModel, 'source'>): boolean =>
    !isEfakturaBilag(bilag);

// Helper to check if bilag counts toward costs
export const bilagCountsTowardCosts = (bilag: BilagModel): boolean => {
    // Only approved bilag count toward costs. E-faktura bilag are excluded: the
    // materials created from the invoice already carry the cost.
    return bilag.approvalStatus === 'approved' && !isEfakturaBilag(bilag);
};

// Helper to check if bilag can be synced to ERP
export const canSyncBilag = (bilag: BilagModel): boolean => {
    // E-faktura bilag are never synced; the invoice is booked as a supplier invoice.
    return bilag.approvalStatus === 'approved' &&
        !isEfakturaBilag(bilag) &&
        bilag.syncStatus !== 'synced' &&
        bilag.syncStatus !== 'syncing';
};
